import { useEffect, useMemo } from 'react';
import { Grid2 } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import moment from 'moment';
import ExpenseDatePicker from './ExpenseDatePicker';
import { COLORS } from '../../../utils/Application_Constants';
import { getSqlMinDate, getSqlMaxDate } from './DateConstraints';

const VendorPaymentEndDate = (props) => {
    const { index, isRequired, isViewOnly = false, isInvoiceReviewLocked = false, expense } = props;
    const { control, errors, watch, setError, clearErrors } = useFormContext({ mode: 'onChange' });
    const expenseCategory = watch(`expenseEntries[${index}].category`);
    const startDate = watch(`expenseEntries[${index}].startDate`) ?? expense.startDate;
    const isTravelExpense = expenseCategory === 'Travel';

    const businessMaxDate = isTravelExpense ? moment().add(6, 'months').endOf('day') : undefined;
    const businessMinEndDate = useMemo(() => {
        if (startDate) {
            return moment(startDate);
        }
        if (isTravelExpense) {
            return moment().subtract(6, 'months').startOf('day');
        }
        return undefined;
    }, [startDate, isTravelExpense]);

    // Get fresh SQL constraint objects to prevent mutation
    const sqlMinDate = getSqlMinDate();
    const sqlMaxDate = getSqlMaxDate();

    // Apply the most restrictive constraints
    const maxEndDate =
        businessMaxDate && businessMaxDate.isBefore(sqlMaxDate) ? businessMaxDate : sqlMaxDate;
    const minEndDate =
        businessMinEndDate && businessMinEndDate.isAfter(sqlMinDate)
            ? businessMinEndDate
            : sqlMinDate;

    const validateEndDate = (value) => {
        if (!value) return true;

        if (startDate && moment(value).isBefore(startDate))
            return 'End Date cannot come before Start Date';

        if (minEndDate && moment(value).isBefore(minEndDate)) return `Minimum date reached`;

        return true;
    };

    const handleValidation = (newValue) => {
        const validationResult = validateEndDate(newValue);

        if (validationResult !== true) {
            return setError(`expenseEntries[${index}].endDate`, {
                type: 'manual',
                message: validationResult,
            });
        }

        return clearErrors(`expenseEntries[${index}].endDate`);
    };

    useEffect(() => {
        const currentEndDate = watch(`expenseEntries[${index}].endDate`);
        handleValidation(currentEndDate);
    }, [startDate]);

    return (
        <Grid2 item xs={12} md={6}>
            <Controller
                disabled={isViewOnly || isInvoiceReviewLocked}
                control={control}
                name={`expenseEntries[${index}].endDate`}
                defaultValue={expense.endDate || null}
                rules={{
                    required:
                        isRequired || isTravelExpense
                            ? 'End Date is required. Please enter a valid date.'
                            : false,
                    validate: {
                        minDate: validateEndDate,
                        maxDate: (value) =>
                            !value ||
                            !maxEndDate ||
                            moment(value).isSameOrBefore(maxEndDate) ||
                            'Maximum date reached',
                    },
                }}
                render={({ onChange, value }) => (
                    <ExpenseDatePicker
                        onChange={(newValue) => {
                            onChange(newValue);
                            handleValidation(newValue);
                        }}
                        value={value ? moment(value) : null}
                        disabled={isViewOnly || isInvoiceReviewLocked}
                        id={`end-date-${index}`}
                        label="End Date"
                        required={isRequired || isTravelExpense}
                        minDate={minEndDate}
                        maxDate={maxEndDate}
                        parentTextFieldProps={{
                            error: Boolean(errors.expenseEntries?.[index]?.endDate),
                            helperText: errors.expenseEntries?.[index]?.endDate?.message,
                        }}
                        parentSx={{
                            '& .MuiPickersInputBase-root': {
                                borderColor: !errors.expenseEntries?.[index]?.endDate
                                    ? 'default'
                                    : `${COLORS.LT_PEPPER} !important`,
                            },
                        }}
                    />
                )}
            />
        </Grid2>
    );
};

export default VendorPaymentEndDate;
